import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { MessageSquare } from 'lucide-react'
import { CommentItem } from './CommentItem'
import type { BudgetComment } from '../../types'

interface CommentListProps {
  comments: BudgetComment[]
  onUpdate: (commentId: number, text: string) => void | Promise<void>
  onDelete: (commentId: number) => void | Promise<void>
}

const ListContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
`

const EmptyIconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #8c8c8c;
  gap: 12px;

  svg {
    height: 24px;
    opacity: 0.5;
  }

  div {
    font-size: 14px;
    color: #8c8c8c;
  }
`

/**
 * List of comments
 */
export const CommentList: React.FC<CommentListProps> = ({ comments, onUpdate, onDelete }) => {
  const { t } = useTranslation()
  const listRef = useRef<HTMLDivElement>(null)

  // Scroll to bottom when comments change
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [comments])

  if (comments.length === 0) {
    return (
      <EmptyIconWrapper>
        <MessageSquare size={28} />
        <div>{t('global:noComments', 'No comments yet')}</div>
      </EmptyIconWrapper>
    )
  }

  return (
    <ListContainer ref={listRef}>
      {comments.map(comment => (
        <CommentItem
          key={comment.id}
          comment={comment}
          onUpdate={text => onUpdate(comment.id, text)}
          onDelete={() => onDelete(comment.id)}
        />
      ))}
    </ListContainer>
  )
}
